import { format } from 'date-fns-tz'

import i18n from '@/i18n'

import { cutTooLong, getProperRoleForChat } from '../../helpers/helpers'
import { getAssetLink } from '../../helpers/get-asset-link'
import StatusPill from '../StatusPill/StatusPill'

export const createFormData = (obj) => {
  const formData = new FormData()
  formData.append('file', obj)
  return formData
}

export const removeDuplicate = (arr) => {
  const tempArr = []
  return arr
    .map((obj) => {
      if (!tempArr.find((some) => some._id === obj._id)) {
        tempArr.push(obj)
        return obj
      } else {
        return undefined
      }
    })
    .filter((x) => x)
}

export const getMediaUrl = (media) => {
  if (media) {
    return media.getContentTemporaryUrl().then((url) => {
      return url
    })
  } else {
    return null
  }
}

export const getDate = (date) => {
  if (format(new Date(date), 'MM/dd/yy') === format(new Date(), 'MM/dd/yy')) {
    return format(new Date(date), 'h:mm a')
  } else {
    return format(new Date(date), 'MM/dd/yy h:mm a')
  }
}

export const getProperTeamType = (role) => {
  switch (role) {
    case 'PropertyManager':
      return 'PMT'
    case 'ShiftManager':
      return 'FDC'
    case 'Staff':
      return 'MT'
    default:
      return ''
  }
}

export const getReverseProperTeamType = (role) => {
  switch (role) {
    case 'PMT':
      return 'PropertyManager'
    case 'FDC':
      return 'ShiftManager'
    case 'MT':
      return 'Staff'
    default:
      return ''
  }
}

export const getProperTeamNameByType = (teamType) => {
  if (teamType.startsWith('PMT')) return i18n.t('chat:teamNames.propertyManagementTeam')
  if (teamType.startsWith('FDC')) return i18n.t('chat:teamNames.frontDeskConcierge')
  if (teamType.startsWith('MT')) return i18n.t('chat:teamNames.maintenanceTeam')
  if (teamType.startsWith('CUST')) return i18n.t('chat:teamNames.customInbox')
  return null
}

export const getSpecificChatTypeFromConversation = (conversation) => {
  const mtrqIndex = conversation.channelName.indexOf('MTRQ')
  if (mtrqIndex !== -1) {
    return 'MTRQ'
  }
  return conversation.channelName.split('_')[1]
}

export const getChatTypeFromConversation = (conversation) => {
  const mtrqIndex = conversation.channelName.indexOf('MTRQ')
  if (mtrqIndex !== -1) {
    return 'MT'
  }
  return conversation.channelName.split('_')[1]
}

export const isMaintenanceRequestChatType = (conversation) => {
  const mtrqIndex = conversation.channelName.indexOf('MTRQ')
  if (mtrqIndex !== -1) {
    return true
  }
  return false
}

export const isMaintenanceGroupChatType = (conversation) => {
  const mtIndex = conversation.channelName.indexOf('_MT')
  const mtrqIndex = conversation.channelName.indexOf('_MTRQ')
  if (mtIndex !== -1 && mtrqIndex === -1) {
    return true
  }
  return false
}

export const chatTypeToIconMap = {
  ALL: 'unified_inbox',
  PMT: 'propertymanager_inbox',
  FDC: 'frontdesk_inbox',
  MT: 'maintenance_inbox',
  CUST: 'custom_inbox',
}

export const checkIfShouldSkipChat = (type, role) => {
  switch (role) {
    case 'ShiftManager':
      return type === 'PMT' || type === 'MT'
    case 'Staff':
      return type === 'PMT' || type === 'FDC'
    default:
      return false
  }
}

export const smallChatOptions = (
  isConversationClosed,
  assignedPerson,
  handleAssign,
  handleOpenOrClose,
) => {
  const options = [
    {
      id: 0,
      text: cutTooLong(assignedPerson, 20),
      icon: 'guest',
      iconStyles: { viewBox: '0 0 28 24' },
    },
    {
      id: 1,
      text: assignedPerson
        ? i18n.t('chat:smallChatOptions.reassign')
        : i18n.t('chat:smallChatOptions.assign'),
      icon: 'guest',
      iconStyles: { viewBox: '0 0 28 24' },
      action: handleAssign,
    },
    {
      id: 2,
      text: isConversationClosed
        ? i18n.t('chat:smallChatOptions.openConversation')
        : i18n.t('chat:smallChatOptions.closeConversation'),
      icon: `${isConversationClosed ? 'circleArrows' : 'checkmark'}`,
      iconStyles: isConversationClosed ? { viewBox: '0 0 10 20' } : { viewBox: '-3 0 40 35' },
      action: handleOpenOrClose,
    },
  ]
  if (!assignedPerson) {
    options.shift()
  }
  return options
}

export const getProperStaffUsers = (currentTeam, managementUsers) => {
  switch (currentTeam) {
    case 'PMT':
      return [
        ...managementUsers
          .filter((managementUser) => managementUser.user.role === 'PropertyManager')
          .map((managementUser) => managementUser.user),
      ]
    case 'FDC':
      return [
        ...managementUsers
          .filter((managementUser) =>
            ['PropertyManager', 'ShiftManager'].includes(managementUser.user.role),
          )
          .map((managementUser) => managementUser.user),
      ]
    case 'MT':
      return [
        ...managementUsers
          .filter((managementUser) =>
            ['Staff', 'PropertyManager'].includes(managementUser.user.role),
          )
          .map((managementUser) => managementUser.user),
      ]
    default:
      return managementUsers.map((managementUser) => managementUser.user)
  }
}

export const conversationStatusMap = {
  OPEN: 'OPEN',
  ARCHIVED: 'ARCHIVED',
  CLOSED: 'CLOSED',
}

export const conversationsFilters = [
  { value: 'open', label: i18n.t('chat:conversationsFilters.open') },
  { value: 'closed', label: i18n.t('chat:conversationsFilters.closed') },
  { value: 'archived', label: i18n.t('chat:conversationsFilters.archived') },
  { value: 'assignedToYou', label: i18n.t('chat:conversationsFilters.assignedToYou') },
  { value: 'unassigned', label: i18n.t('chat:conversationsFilters.unassigned') },
]

export const renderProperStatusPill = (status) => {
  switch (status) {
    case 'Finished':
      return <StatusPill color="#36B42B" title={i18n.t('chat:maintenanceStatus.finished')} />
    case 'In Progress':
      return <StatusPill color="#FFB343" title={i18n.t('chat:maintenanceStatus.inProgress')} />
    case 'On Hold':
      return <StatusPill color="#FFB343" title={i18n.t('chat:maintenanceStatus.onHold')} />
    case 'Pending':
      return <StatusPill color="#FFB343" title={i18n.t('chat:maintenanceStatus.pending')} />
    case 'Canceled':
      return <StatusPill color="#E53B15" title={i18n.t('chat:maintenanceStatus.canceled')} />
    case 'Unassigned':
      return <StatusPill color="#A5A5A5" title={i18n.t('chat:maintenanceStatus.unassigned')} />
    case 'Assigned':
      return <StatusPill color="#4383FF" title={i18n.t('chat:maintenanceStatus.assigned')} />
    case 'Scheduled':
      return <StatusPill color="#8843F8" title={i18n.t('chat:maintenanceStatus.scheduled')} />
    default:
      return '-'
  }
}

export const getMaintenanceRequestId = (inputString) => {
  const firstUnderscoreIndex = inputString.indexOf('_')
  const secondUnderscoreIndex = inputString.indexOf('_', firstUnderscoreIndex + 1)
  return inputString.substring(secondUnderscoreIndex + 1)
}

export const isHighManagementUser = (role) => {
  return ['Manager', 'Administrator', 'PropertyManager', 'ShiftManager', 'Staff'].includes(role)
}

export const getUserNameById = (
  id,
  selectedChat,
  managementUsers,
  systemMessage = i18n.t('chat:systemMessage'),
) => {
  const chatClientID = selectedChat.channelName.slice(0, selectedChat.channelName.indexOf('_'))
  if (Number(id) === Number(chatClientID)) {
    return selectedChat.name
  } else if (id === 'system') {
    return systemMessage
  } else {
    const user = managementUsers
      .map((managementPerson) => (managementPerson.user ? managementPerson.user : managementPerson))
      .find((contact) => contact.id === Number(id) || contact.email === id)
    return user
      ? isHighManagementUser(user.role)
        ? `${getProperRoleForChat(user.role)}`
        : `${user.firstName} ${user.lastName}`
      : null
  }
}

export const getAvatarById = (id, selectedChat, managementUsers) => {
  const chatClientID = selectedChat.channelName.slice(0, selectedChat.channelName.indexOf('_'))
  if (id === chatClientID) {
    return selectedChat.avatar
  } else {
    const user = managementUsers
      .map((managementPerson) => (managementPerson.user ? managementPerson.user : managementPerson))
      .find((contact) => contact.id === Number(id))
    return getAssetLink({
      path: 'chitown-avatar/avatar',
      file: user?.avatar,
      fallback: { file: 'avatar.png' },
    })
  }
}

export const convertMessageProperly = async (newMessage, selectedChat, managementUsers) => {
  return {
    ...newMessage,
    user: {
      ...newMessage.user,
      name: getUserNameById(newMessage.user._id, selectedChat, managementUsers),
      avatar: getAvatarById(newMessage.user._id, selectedChat, managementUsers),
    },
    image: await getMediaUrl(newMessage.media),
  }
}

export const isWithinLast30Seconds = (dateString) => {
  const createdAtDate = new Date(dateString)
  const currentDate = new Date()
  const differenceInMilliseconds = currentDate - createdAtDate
  return differenceInMilliseconds <= 30000
}

export const convertConversation = (convs, { chatContacts = [], personsData = [] }) => {
  return convs
    .map((conv) => {
      const person =
        chatContacts &&
        [...personsData, ...chatContacts].find(
          (contact) =>
            Number(contact.id) ===
            Number(
              conv.channelName
                ? conv.channelName.slice(0, conv.channelName.indexOf('_'))
                : conv.id.slice(0, conv.id.indexOf('_')),
            ),
        )

      return {
        ...conv,
        channelName: conv.channelName ? conv.channelName : conv.id,
        avatar: person ? person.avatar : '',
        name: person ? `${person.firstName} ${person.lastName}` : '',
        role: person ? person.role : '',
        email: person ? person.email : '',
        phone: person ? person.phone : '',
        apartment: person ? person.apartment : '',
        address: person ? `${person.street} #${person.apartment}` : '',
        buildingData: person
          ? { name: person.buildingName, id: person.buildingId, timezone: person.timezone }
          : null,
        lastMessage: conv.lastMessage ? conv.lastMessage : '',
        lastMessageStatus: conv.lastMessageStatus || '',
      }
    })
    .filter((conv) => conv.name)
    .sort(
      (a, b) => Number(b.lastMessage ? b.updatedAt : 0) - Number(a.lastMessage ? a.updatedAt : 0),
    )
}
